import type { LoadedPost } from "./types";

const enModules = import.meta.glob("../content/posts/*.en.md", {
  query: "?raw",
  import: "default",
  eager: false,
});
const frModules = import.meta.glob("../content/posts/*.fr.md", {
  query: "?raw",
  import: "default",
  eager: false,
});

function parseFrontmatter(rawInput: unknown): { data: Record<string, unknown>; content: string } {
  const raw = String(rawInput ?? "").replace(/^\uFEFF/, "").trimStart();
  const match = raw.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/);
  if (!match) return { data: {}, content: raw };

  const [, fmBlock, body] = match;
  const data: Record<string, unknown> = {};
  const lines = fmBlock.split(/\r?\n/);

  let currentKey: string | null = null;
  let currentNested: Record<string, unknown> | null = null;

  const stripQuotes = (v: string): string => {
    const t = v.trim();
    if ((t.startsWith('"') && t.endsWith('"')) || (t.startsWith("'") && t.endsWith("'"))) {
      return t.slice(1, -1);
    }
    return t;
  };

  const parseValue = (v: string): unknown => {
    const t = v.trim();
    if (!t) return "";
    if (t.startsWith("[") && t.endsWith("]")) {
      const inner = t.slice(1, -1).trim();
      if (!inner) return [];
      return inner.split(",").map(s => stripQuotes(s.trim()));
    }
    return stripQuotes(t);
  };

  for (const line of lines) {
    if (!line.trim() || line.trim().startsWith("#")) continue;
    const nestedMatch = line.match(/^(\s+)([A-Za-z0-9_-]+):\s*(.*)$/);
    if (nestedMatch && currentNested) {
      const [, , k, v] = nestedMatch;
      currentNested[k] = parseValue(v);
      continue;
    }
    const topMatch = line.match(/^([A-Za-z0-9_-]+):\s*(.*)$/);
    if (topMatch) {
      const [, k, v] = topMatch;
      if (v.trim() === "") {
        currentNested = {};
        data[k] = currentNested;
        currentKey = k;
      } else {
        data[k] = parseValue(v);
        currentNested = null;
        currentKey = k;
      }
    }
  }

  return { data, content: body };
}

async function loadAll(
  globMap: Record<string, () => Promise<unknown>>,
  lang: "en" | "fr"
): Promise<LoadedPost[]> {
  const entries = Object.entries(globMap);
  const items = await Promise.all(
    entries.map(async ([path, loader]) => {
      try {
        const raw = await loader();
        const { data, content } = parseFrontmatter(raw);
        const fm = data;
        const fallbackSlug = path.split("/").pop()?.replace(/\.(en|fr)\.md$/, "") || "post";
        return {
          frontmatter: {
            title: typeof fm.title === "string" ? fm.title : "Untitled",
            date: typeof fm.date === "string" ? fm.date : "1970-01-01",
            slug: typeof fm.slug === "string" ? fm.slug : fallbackSlug,
            excerpt: typeof fm.excerpt === "string" ? fm.excerpt : undefined,
            tags: Array.isArray(fm.tags) ? fm.tags.map(String) : undefined,
            hero: typeof fm.hero === "string" ? fm.hero : undefined,
          },
          content: content.trim(),
          lang,
        } satisfies LoadedPost;
      } catch (err) {
        console.error(`Failed to parse blog post ${path}:`, err);
        return null;
      }
    })
  );
  return items
    .filter((p) => p !== null)
    .map((p) => p as LoadedPost)
    .sort((a, b) => (a.frontmatter.date < b.frontmatter.date ? 1 : -1));
}

export async function loadPostsByLang(lang: "en" | "fr") {
  return lang === "en" ? loadAll(enModules, "en") : loadAll(frModules, "fr");
}

export async function loadPost(slug: string, lang: "en" | "fr") {
  const posts = await loadPostsByLang(lang);
  return posts.find((p) => p.frontmatter.slug === slug) || null;
}
